var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @next/next/no-img-element */
import { GetCommonSettings } from '@themes/common/data';
import React, { useEffect, useRef, forwardRef } from 'react';
import QRCode from 'qrcode';
import { FormatMoney } from '@components/Shared/Utils/price';
import { StringReplaceCurlyBraces } from '@commons/replaceCurly';
import { format } from 'date-fns';
const PrintTemplate = forwardRef(({ productData, onBarcodeLoaded }, ref) => {
    const { translations: { print: translations }, print: printSettings, } = GetCommonSettings();
    const qrCodeRef = useRef(null);
    const { variant_id, title, price, compare_at_price, sku, unit_factor, unit, url: productPath, variant_options } = productData;
    let newTitle = title;
    if (variant_options) {
        const variantOptions = Object.values(variant_options).filter((option) => option && option !== 'Default Title');
        if (variantOptions.length > 0 && !variant_options.includes('Default Title')) {
            newTitle += ' ' + variantOptions.join(' ');
        }
    }
    const price_symbol = FormatMoney(0, undefined, false, true, true);
    const compare_at_price_display = FormatMoney(compare_at_price * unit_factor);
    const price_display = FormatMoney(price * unit_factor, undefined, false, false);
    const discountPercentage = Math.round(((compare_at_price - price) / compare_at_price) * 100);
    const discountPrice = FormatMoney(compare_at_price - price);
    const discountDisplay = StringReplaceCurlyBraces(translations.save, {
        percentage: discountPercentage,
        price: discountPrice,
    });
    const hasDiscount = compare_at_price && compare_at_price > price;
    useEffect(() => {
        if (qrCodeRef.current) {
            const productUrl = `${window.location.origin}${productPath}`;
            const productUrlWithParams = new URL(productUrl);
            productUrlWithParams.searchParams.set('variant_id', variant_id === null || variant_id === void 0 ? void 0 : variant_id.toString());
            const loadCanvas = () => __awaiter(void 0, void 0, void 0, function* () {
                yield QRCode.toCanvas(qrCodeRef.current, productUrlWithParams.toString(), {
                    width: 200,
                    margin: 1,
                });
                onBarcodeLoaded === null || onBarcodeLoaded === void 0 ? void 0 : onBarcodeLoaded();
            });
            loadCanvas();
        }
    }, [onBarcodeLoaded, productPath, variant_id]);
    return (_jsxs("div", Object.assign({ className: 'print-template', ref: ref }, { children: [_jsx("style", Object.assign({ jsx: true }, { children: `
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');

        @page {
          size: A4 portrait;
          background-color: #fff;
        }
        .print-body {
          size: A4 portrait;
          background-color: #fff;
          margin: 0mm;
        }
        .print-template {
          width: 297mm; // 297mm.
          height: 210mm; // 210mm.
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          font-family: 'Roboto', Arial, Helvetica, sans-serif;
          background-color: #fff;
          position: relative;
          page-break-after: always;
          overflow: hidden;
          border-bottom: 1px solid #000;
        }
        .product-print--body {
          display: flex;
          position: relative;
          border-bottom: 1px solid #000;
        }
        .product-print--left-container {
          display: flex;
          position: relative;
          flex-direction: column;
          width: 80%;
          height: 165mm;
          border-right: 1px solid #000;
          padding-top: 20mm;
          padding-left: 20mm;
        }
        /* SALE banner (top-left) */
        .discount-container {
          display: inline-block;
          background-color: #d6591f; /* Stenbolaget brand orange */
          color: #ffffff;
          /* Top 3mm, Right (10mm + 50px), Bottom 3mm, Left 4mm */
          padding: 3mm calc(10mm + 50px) 3mm 4mm;
          border-radius: 0;
          width: fit-content;
        }
        .discount-title {
          color: #ffffff;
          font-size: 14pt;
          font-weight: 500;
          margin-bottom: 2pt;
          letter-spacing: 0.3pt;
        }
        .price-container {
          position: absolute;
          top: 80mm;
        }
        .regular-price--container {
          display: flex;
          gap: 20pt;
          align-items: flex-end;
        }
        .regular-price--container .price {
          font-size: 82pt;
          font-weight: 400;
          line-height: 60pt;
        }
        .regular-price--container .price-symbol {
          font-size: 24pt;
          font-weight: 500;
        }
        .compare-price {
          text-decoration: line-through;
          font-size: 24pt;
          font-weight: 300;
          color: #ffffff; /* ensure white in orange banner */
        }
        .discount-percentage {
          font-size: 32pt;
          font-weight: 700;
          color: #ffffff;
          margin-top: 2pt;
        }
        /* Emphasize the NU! label above the price */
        .price-sale {
          color: #d6591f; /* brand orange */
          font-size: 28pt;
          font-weight: 700;
          margin-bottom: 6pt;
          letter-spacing: 0.5pt;
        }
        .qr-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .qr-code {
          background: white;
        }
        .title-container {
          display: flex;
          gap: 10pt;
          margin-top: auto;
          height: 40mm;
        }
        .title-container .title {
          font-size: 32pt;
          font-weight: 400;
        }
        .product-print--right-container {
          display: flex;
          flex-direction: column;
          padding: 5mm;
          width: 20%;
          height: auto;
          justify-items: center;
        }
        .sku--container {
          margin-top: auto;
          height: 20mm;
        }
        .product-print--footer {
          display: flex;
          position: relative;
          height: 10mm;
          padding: 5mm;
        }
        .product-print--footer .logo-container {
          height: 20mm;
        }
        .product-print--footer .logo {
          height: 10mm;
        }
        .product-print--footer .date-container {
          height: 10mm;
          width: 20%;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .product-print--footer .sale-info-container {
          width: 20%;
          padding-left: 5mm;
        }
        .product-print--footer .logo-container {
          margin: 0 auto;
        }
      ` })), _jsxs("div", Object.assign({ className: 'product-print--body' }, { children: [_jsxs("div", Object.assign({ className: 'product-print--left-container' }, { children: [hasDiscount && (_jsxs("div", Object.assign({ className: 'discount-container' }, { children: [translations.compare_price_sale_title && _jsx("div", Object.assign({ className: 'discount-title' }, { children: translations.compare_price_sale_title })), _jsxs("div", Object.assign({ className: 'compare-price' }, { children: [compare_at_price_display, "/", unit] })), _jsx("div", Object.assign({ className: 'discount-percentage' }, { children: discountDisplay }))] }))), _jsxs("div", Object.assign({ className: 'price-container' }, { children: [hasDiscount && translations.price_sale_title && _jsx("div", Object.assign({ className: 'price-sale' }, { children: translations.price_sale_title })), _jsxs("div", Object.assign({ className: 'regular-price--container' }, { children: [_jsx("div", Object.assign({ className: 'price' }, { children: price_display })), _jsxs("div", Object.assign({ className: 'price-symbol' }, { children: [price_symbol, "/", unit] }))] }))] })), _jsx("div", Object.assign({ className: 'title-container' }, { children: _jsx("div", Object.assign({ className: 'title' }, { children: newTitle })) }))] })), _jsxs("div", Object.assign({ className: 'product-print--right-container' }, { children: [_jsx("div", Object.assign({ className: 'qr-container' }, { children: _jsx("canvas", { ref: qrCodeRef, className: 'qr-code' }) })), _jsxs("div", Object.assign({ className: 'sku--container' }, { children: [translations.sku && _jsx("div", Object.assign({ className: 'sku-title' }, { children: translations.sku })), _jsx("div", Object.assign({ className: 'sku' }, { children: sku }))] }))] }))] })), _jsxs("div", Object.assign({ className: 'product-print--footer' }, { children: [_jsx("div", Object.assign({ className: 'date-container' }, { children: format(new Date(), 'yyyy-MM-dd') })), _jsx("div", Object.assign({ className: 'logo-container' }, { children: printSettings.logo && _jsx("img", { src: printSettings.logo, alt: 'stenbolaget', className: 'logo' }) })), _jsx("div", Object.assign({ className: 'sale-info-container' }, { children: hasDiscount ? translations.sale_info : '' }))] }))] })));
});
PrintTemplate.displayName = 'PrintTemplate';
export default PrintTemplate;
